import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Settings } from 'lucide-react';

interface TranspositionSettingsProps {
  settingsOpen: boolean;
  onSettingsOpenChange: (open: boolean) => void;
  fontSize: string;
  onFontSizeChange: (size: string) => void;
  chordFontSize: number;
  onChordFontSizeChange: (size: number) => void;
  chordColor: string;
  onChordColorChange: (color: string) => void;
  columns: string;
  onColumnsChange: (columns: string) => void;
}

export const TranspositionSettings: React.FC<TranspositionSettingsProps> = ({
  settingsOpen,
  onSettingsOpenChange,
  fontSize,
  onFontSizeChange,
  chordFontSize,
  onChordFontSizeChange,
  chordColor,
  onChordColorChange,
  columns,
  onColumnsChange,
}) => {
  return (
    <div className="space-y-3">
      <Collapsible open={settingsOpen} onOpenChange={onSettingsOpenChange}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-start gap-2 -ml-4 hover:bg-muted">
            <Settings className="h-5 w-5 text-primary" />
            <span className="text-base font-semibold">Visningsinnstillinger</span>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-6 pt-4 pl-9">
            {/* Fontstørrelser */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Tekststørrelse</Label>
                  <span className="text-sm text-muted-foreground">{fontSize} pt</span>
                </div>
                <Slider
                  value={[parseInt(fontSize)]}
                  onValueChange={(value) => onFontSizeChange(value[0].toString())}
                  min={10}
                  max={24}
                  step={1}
                  className="w-full"
                />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Akkord-størrelse</Label>
                  <span className="text-sm text-muted-foreground">{chordFontSize} pt</span>
                </div>
                <Slider
                  value={[chordFontSize]}
                  onValueChange={(value) => onChordFontSizeChange(value[0])}
                  min={12}
                  max={32}
                  step={1}
                  className="w-full"
                />
              </div>
            </div>

            {/* Andre innstillinger */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Akkordfarge</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={chordColor}
                    onChange={(e) => onChordColorChange(e.target.value)}
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={chordColor}
                    onChange={(e) => onChordColorChange(e.target.value)}
                    className="flex-1 text-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Kolonner</Label>
                <Select value={columns} onValueChange={onColumnsChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 kolonne</SelectItem>
                    <SelectItem value="2">2 kolonner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

